import { CurrencySelect } from '@/views/screens/common';
import { Col, Form, Row } from 'antd';
import moment from 'moment';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { USER_PREFERENCES } from '../../../../../../../contants/common';
import { getNewSpotQuoteNoInProgress } from '../../../../../../../redux/common/CommonActions';
import useUserPreference from '../../../../../../../redux/hooks/useUserPreference';
import { formateWithMoment } from '../../../../../../../utilz/dates';
import { routeFileSelector } from '../../../../../../../utilz/i18n';
import FormInput from '../../../../../../components/Inputs/FormInput';
import HiddenFormInput from '../../../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../../../../components/Labels/SecondaryLabel';
import HalWarningModal from '../../../../../../components/Modals/HalWarningModal';
import BidDurationModal from '../../../../../Shipment/components/QuoteCard/components/QuoteDetails/BidDurationModal';
import { QUOTE_STATUSES } from '../../../../../Shipment/components/QuoteCard/constants';
import useBidDuration, { initialBidDurationTimeError } from '../../../../hooks/useBidDuration';
import { BidDurationCard } from '../../../common/BidDurationCard';
import { bidExpirationRules, quoteTitleRules } from './constants';

const HallQuoteDetailsForm = () => {
  const [showBidDuration, setShowBidDuration] = useState(false);
  const { selectedCompany } = useSelector((state) => state.auth);
  const { quoteNo, quoteTitle } = useSelector((state) => state.common.currentSpotQuoteNoAndTitle.data);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth.user.data, shallowEqual);

  const [form] = Form.useForm();
  const createdOn = Form.useWatch('createdOn', form);
  const bidExpirationDateValue = form.getFieldValue('bidExpirationDate');
  const bidExpirationTimeZoneInfo = form.getFieldValue('bidExpirationTimeZoneInfo');

  const { data } = useSelector((state) => state.shipment.shipmentDetails);
  const copyQuoteKey = useSelector((state) => state.common.copyQuoteKey);
  const createdByTitle = `${userData.firstName} ${userData.lastName}`;

  useEffect(() => {
    if (copyQuoteKey) {
      form.setFields([
        { name: 'currencyCode', value: data?.currencyCode },
        { name: 'bidExpirationDate', value: null },
      ]);
    }
  }, []);

  useEffect(() => {
    if (selectedCompany?.companyId) {
      form.setFieldValue('customerKey', selectedCompany?.companyId);
    }
  }, [selectedCompany]);

  useEffect(() => {
    dispatch(getNewSpotQuoteNoInProgress());
  }, [dispatch]);

  useEffect(() => {
    quoteNo && form.setFieldValue('quoteNo', quoteNo);
    quoteTitle && form.setFieldValue('quoteTitle', quoteTitle);
  }, [quoteNo, quoteTitle, form]);

  useEffect(() => {
    userData && form.setFieldValue('createdBy', createdByTitle);
  }, [form, userData]);

  const userDateFormat = useUserPreference(USER_PREFERENCES.DEFAULT_DATE_FORMAT);

  const { calculatedBidExpirationDate, handleOkBidDurationModal, bidDurationTimeError, setBidDurationTimeError } =
    useBidDuration({ form, setShowBidDuration });

  return (
    <>
      <Form
        form={form}
        name="quoteDetailsForm"
        initialValues={{
          createdBy: createdByTitle,
          createdOn: moment(new Date()),
          shipmentNumber: '',
          shipmentKey: 0,
          shipmentStatus: null,
          status: QUOTE_STATUSES.new,
          customerKey: 0,
          quoteNo: '',
        }}
      >
        <Row gutter={30}>
          <Col sm={6}>
            <Row gutter={15}>
              <Col sm={10}>
                <FormInput
                  label={t('translation:default.fields.quoteNo')}
                  editMode={false}
                  margin="m-0"
                  name="quoteNo"
                  data-testid="quote-no-input"
                />
              </Col>
              <Col sm={14}>
                <FormInput
                  name="quoteTitle"
                  label={t('translation:default.fields.quoteTitle')}
                  editMode
                  margin="m-0"
                  rules={quoteTitleRules}
                  maxLength="250"
                  data-testid="quote-title-input"
                />
              </Col>
            </Row>
          </Col>
          <Col sm={12}>
            <Row gutter={15}>
              <Col sm={6}>
                <FormInput
                  name="status"
                  label={t('translation:default.fields.status.label')}
                  editMode={false}
                  margin="m-0"
                  data-testid="quote-status-input"
                />
              </Col>
              <Col sm={6}>
                <CurrencySelect
                  editMode
                  label={t('translation:default.fields.currency.label')}
                  name="currencyCode"
                  margin="mb-15"
                  rules={[{ required: true, message: 'Currency is required' }]}
                />
              </Col>
              <Col
                sm={12}
                onClick={() => setShowBidDuration(true)}
                className="d-flex flex-direction-column"
                style={{
                  cursor: bidExpirationDateValue ? 'pointer' : 'default',
                }}
                data-testid="quote-details-bid-expiration"
              >
                <Form.Item
                  name="bidExpiration"
                  rules={bidExpirationRules(bidExpirationDateValue, bidExpirationTimeZoneInfo)}
                >
                  <BidDurationCard dateValue={calculatedBidExpirationDate || bidExpirationDateValue} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col sm={6}>
            <Row gutter={15}>
              <Col sm={10}>
                <FormInput
                  name="createdBy"
                  label={t('translation:getQuote.quoteDetails.createdBy.label')}
                  editMode={false}
                  margin="m-0"
                  data-testid="quote-created-by"
                />
              </Col>
              <Col sm={14} data-testid="quote-created-on">
                <FormLabel>{t('translation:default.fields.createdOn.title')}</FormLabel>
                <div>
                  <SecondaryLabel className="f-14 fw-600 text-dark" dataTestID="user-date-format">{`${formateWithMoment(
                    createdOn,
                    userDateFormat,
                  )} | ${formateWithMoment(createdOn, 'hh:mm:ss A')}`}</SecondaryLabel>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
        <HiddenFormInput name="createdOn" />
        <HiddenFormInput name="customerKey" />
        <HiddenFormInput name="bidExpirationDate" />
        <HiddenFormInput name="bidExpirationOffsetHours" />
        <HiddenFormInput name="bidExpirationTimeZoneInfo" />
      </Form>
      <HalWarningModal
        open={bidDurationTimeError.showModal}
        content={bidDurationTimeError.title}
        onClose={() => setBidDurationTimeError(initialBidDurationTimeError)}
        onOk={() => setBidDurationTimeError(initialBidDurationTimeError)}
        okText="Ok"
      />
      <BidDurationModal
        alertTitle="Bid Duration"
        open={showBidDuration}
        onClose={() => setShowBidDuration(false)}
        onOk={handleOkBidDurationModal}
        okText="Submit"
        setUserTZInitially
      />
    </>
  );
};

export default memo(HallQuoteDetailsForm);
